import { useRef } from 'react';

export interface CommitDiffGate {
    /** Forget the baseline — the next commit always persists (fresh doc / regenerate / re-load). */
    reset: () => void;
    /** Snapshot `content` as the last-committed baseline (on load of a committed doc, and after every successful commit). */
    latch: (content: unknown) => void;
    /** Whether a baseline is currently held. */
    isLatched: () => boolean;
    /** True when a baseline exists and `content` serializes identically — the commit can be skipped. */
    isUnchanged: (content: unknown) => boolean;
}

/**
 * Commit-diff gate shared by the document editors (OnePager, PlateDocumentDrawer,
 * useDocumentAutosave): holds a JSON snapshot of the last committed content so a
 * Save & Close of unchanged content skips the server round-trip instead of
 * accreting an identical version in the lineage's history.
 *
 * This owns only the snapshot + comparison. Each consumer keeps its own capture
 * policy — WHAT counts as the baseline and WHEN it is taken (e.g. the drawer
 * latches off the first post-load editor onChange so the comparison is against
 * the converted/normalized shape).
 */
export function useCommitDiffGate(): CommitDiffGate {
    const jsonRef = useRef<string | null>(null);
    // Stable object identity so consumers can list the gate in hook deps freely.
    const gateRef = useRef<CommitDiffGate>({
        reset: () => {
            jsonRef.current = null;
        },
        latch: (content: unknown) => {
            jsonRef.current = JSON.stringify(content);
        },
        isLatched: () => jsonRef.current !== null,
        isUnchanged: (content: unknown) => jsonRef.current !== null && JSON.stringify(content) === jsonRef.current,
    });
    return gateRef.current;
}
